import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Folder } from '@/assets/icons'
import * as Dialog from '@radix-ui/react-dialog'
import { ButtonLoader } from '@/components/loaders/ButtonLoader'

type CreateFolderDialogProps = {
  open: boolean
  onSave: (folderName: string) => void
  onClose: () => void
  isCreating?: boolean
}

type FormInputs = {
  folderName: string
}

const validationSchema = z.object({
  folderName: z.string().trim().min(1, 'Folder name is required'),
})

export function CreateFolderDialog({ open, onSave, onClose, isCreating = false }: CreateFolderDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      folderName: '',
    },
    resolver: zodResolver(validationSchema),
  })

  const handleFormSubmit = (data: FormInputs) => {
    onSave(data.folderName)
    reset()
  }

  const handleClose = () => {
    if (isCreating) return
    reset()
    onClose()
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-black/50 fixed inset-0 z-[999]" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 w-[90vw] max-w-[480px] -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-lg focus:outline-none z-[999] overflow-hidden"
          onEscapeKeyDown={(e) => {
            if (isCreating) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (isCreating) e.preventDefault()
          }}
        >
          <Dialog.Title className="text-left text-xl font-semibold px-6 pt-6 pb-4">New folder</Dialog.Title>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col h-full">
            <div className="px-6 pb-6 flex-grow">
              <div className="flex items-center gap-2 mb-4">
                <Folder className="w-5 h-5 text-brand-neutral-600" />
                <span className="text-base text-brand-neutral-600">Enter a name for the new folder</span>
              </div>

              <input
                id="folderName"
                autoFocus
                {...register('folderName')}
                className="block w-full rounded-md border border-brand-neutral-300 p-3 text-base text-brand-neutral-900 focus:border-brand-500 focus:ring-0 focus:outline-none"
                placeholder="Folder name"
              />
              {errors.folderName && <p className="mt-2 text-sm text-red-700">{errors.folderName.message}</p>}
            </div>

            <div className="flex justify-end gap-3 px-6 pb-4 border-t border-brand-neutral-200 pt-4">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={isCreating}
                  className="px-4 py-2 text-sm font-medium text-brand-neutral-700 bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Cancel folder creation and close dialog"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={isCreating}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 border border-transparent rounded-md hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                aria-label="Create new folder"
              >
                <span>{isCreating ? 'Creating...' : 'Create'}</span>
                <ButtonLoader loading={isCreating} />
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
